import React, { useEffect } from "react";
import "./GamePopoverCard.css";
import { FaCircle, FaDollarSign } from "react-icons/fa";
import { GamePopoverCardProps } from "../const/ui";
import { useGameAIPrediction } from "../pages/AIPredictions";
import { SiOpenai } from "react-icons/si";

const LABELS: Record<string, string> = {
  last10: "Last 10 Games",
  winStreak: "Win Streak",
  runDiff: "Run Differential",
  injuries: "Injuries",
  hypeNews: "Positive News",
  antiHype: "Negative News",
  hypeHits: "Positive Headlines",
  antiHypeHits: "Negative Headlines",
  eliteBonus: "Elite Bonus",
  dominanceBonus: "Division Leader",
  underdogBoost: "Underdog Wins",
  homeFieldBonus: "Home Field Strength",
  roadWarriorBonus: "Road Warrior Strength",
  balancedTeamBonus: "Balanced Team",
  fatiguePenalty: "Fatigue Penalty",
  divisionFavoriteBonus: "Division Favorite",
  wildcardChaserBonus: "Wildcard Chaser",
};



const GamePopoverCard: React.FC<GamePopoverCardProps> = ({
  sport,
  className,
  balls,
  strikes,
  outs,
  pitcher,
  batter,
  onDeck,
  period,
  clock,
  winningPitcher,
  losingPitcher,
  savePitcher,
  status,
  homeScore,
  awayScore,
  homeLogo,
  awayLogo,
  occupiedBases,
  moneyline,
  homeClutchScore,
  awayClutchScore,
  homeClutchBreakdown,
  awayClutchBreakdown,
  network,
  homeTeam,
  awayTeam,
  homeTeamName,
  awayTeamName,
}) => {
  const lowerStatus = status?.toLowerCase();
  const { loading, output, runPrediction } = useGameAIPrediction();

  useEffect(() => {
    runPrediction({
      homeTeamName,
      awayTeamName,
      hypeScores: {
        [homeTeamName]: homeClutchScore ?? 0,
        [awayTeamName]: awayClutchScore ?? 0,
      },
      seriesInfo: status,
      lastGameResult: "",
      injuries: "",
      news: {},
      odds: moneyline,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeTeamName, awayTeamName]);

  return (
    <div className={`popover-card scoreboard-style ${className}`}>
      {/* LIVE or FINAL status */}
      {lowerStatus === "in progress" && (
        <div className="status">
          <span className="live-badge">
            <FaCircle className="live-dot" />
            <span className="live-text">LIVE</span>
          </span>
        </div>
      )}
      {lowerStatus === "final" && (
        <div className="status">
          <span className="final">
            <span>FINAL</span>
          </span>
        </div>
      )}

      {/* Top logos and score */}
      <div className="scoreboard-top">
        <img
          src={awayLogo}
          alt="Away Team"
          className={`team-logo-sm ${awayClutchScore && homeClutchScore && awayClutchScore > homeClutchScore ? "highlighted" : ""}`}
        />
        <span className="team-score">{awayScore}</span>
        <div className="broadcast-center">{network}</div>
        <span className="team-score">{homeScore}</span>
        <img
          src={homeLogo}
          alt="Home Team"
          className={`team-logo-sm ${homeClutchScore && awayClutchScore && homeClutchScore > awayClutchScore ? "highlighted" : ""}`}
        />
      </div>

      {lowerStatus !== 'final' &&
        <div className="ai-prediction-container">
        {loading ? (
          <div className="ai-loading">
            <SiOpenai className="ai-loading-icon" />
            Loading HypeAI Summary...
          </div>
        ) : output ? (
          <div className="ai-output-box">
          <div className="ai-output-header">
            <strong>HypeAI Breakdown</strong>
            <span className="ai-badge">AI</span>
          </div>
          <p>{output}</p>
        </div>
        ) : null}
      </div>
      }

      {/* In progress Baseball */}
      {lowerStatus === "in progress" && sport === "mlb" && (
        <>
          <div className="scoreboard-bottom">
            <div className="scoreboard-meta">
              <span className="meta-inning">{period}th</span>
              <span className="meta-count">{balls}-{strikes}</span>
              <span className="meta-pitch-count">P:{clock}</span>
            </div>
            <div className="scoreboard-runners">
              <div className={`base base-1 ${occupiedBases?.first ? "occupied" : ""}`} />
              <div className={`base base-2 ${occupiedBases?.second ? "occupied" : ""}`} />
              <div className={`base base-3 ${occupiedBases?.third ? "occupied" : ""}`} />
              <div className="base base-home" />
            </div>
            <div className="scoreboard-outs">
              <div className={`out-dot ${outs && outs >= 1 ? "filled" : ""}`} />
              <div className={`out-dot ${outs && outs >= 2 ? "filled" : ""}`} />
              <div className={`out-dot ${outs && outs >= 3 ? "filled" : ""}`} />
            </div>
          </div>

          <div className="scoreboard-extra">
            {pitcher && (
              <div className="popover-section">
                <div className="popover-label">Pitching</div>
                <div className="popover-value with-headshot">
                  {pitcher.headshot && <img src={pitcher.headshot} alt={pitcher.name} className="mini-headshot" />}
                  {pitcher.name}
                </div>
              </div>
            )}
            {batter && (
              <div className="popover-section">
                <div className="popover-label">At Bat</div>
                <div className="popover-value with-headshot">
                  {batter.headshot && <img src={batter.headshot} alt={batter.name} className="mini-headshot" />}
                  {batter.name}
                </div>
              </div>
            )}
            {onDeck && (
              <div className="popover-section">
                <div className="popover-label">On Deck</div>
                <div className="popover-value with-headshot">
                  {onDeck.headshot && <img src={onDeck.headshot} alt={onDeck.name} className="mini-headshot" />}
                  {onDeck.name}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* In progress non-MLB Clock */}
      {lowerStatus === "in progress" && (
        <div className="scoreboard-live-details">
          {(sport === "nba" || sport === "nhl" || sport === "nfl" || sport === "epl") && (
            <>
              <div className="live-meta">
                <div className="live-label">
                  {sport === "nba" || sport === "nfl" ? "Quarter" : sport === "nhl" ? "Period" : "Half"}
                </div>
                <div className="live-value">{period ?? "—"}</div>
              </div>
              {sport !== "epl" && (
                <div className="live-meta">
                  <div className="live-label">Clock</div>
                  <div className="live-value">{clock ?? "—"}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Final Baseball Box Score */}
      {lowerStatus === "final" && sport === "mlb" && (
        <div className="final-box-score">
          <table className="box-score-table">
          <thead>
            <tr>
              <th></th>
              {Array.from({ length: 9 }).map((_, i) => (
                <th key={`inning-${i + 1}`}>{i + 1}</th>
              ))}
              <th>R</th>
              <th>H</th>
              <th>E</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{awayTeam?.abbreviation}</td>
              {awayTeam?.linescores?.map((score: string, idx: number) => (
                <td key={`away-inning-${idx}`}>{score}</td>
              ))}
              {Array.from({ length: 9 - (awayTeam?.linescores?.length ?? 0) }).map((_, idx) => (
                <td key={`away-empty-${idx}`}></td>
              ))}
              <td>{awayTeam?.runs}</td>
              <td>{awayTeam?.hits}</td>
              <td>{awayTeam?.errors}</td>
            </tr>
            <tr>
              <td>{homeTeam?.abbreviation}</td>
              {homeTeam?.linescores?.map((score: string, idx: number) => (
                <td key={`home-inning-${idx}`}>{score}</td>
              ))}
              {Array.from({ length: 9 - (homeTeam?.linescores?.length ?? 0) }).map((_, idx) => (
                <td key={`home-empty-${idx}`}></td>
              ))}
              <td>{homeTeam?.runs}</td>
              <td>{homeTeam?.hits}</td>
              <td>{homeTeam?.errors}</td>
            </tr>
          </tbody>

          </table>

          {/* Pitchers */}
          <div className="final-pitchers">
            {winningPitcher && (
              <div className="pitcher-info">
                <span className="pitcher-label">Winning Pitcher:</span> {winningPitcher.fullName}
              </div>
            )}
            {losingPitcher && (
              <div className="pitcher-info">
                <span className="pitcher-label">Losing Pitcher:</span> {losingPitcher.fullName}
              </div>
            )}
            {savePitcher && (
              <div className="pitcher-info">
                <span className="pitcher-label">Save:</span> {savePitcher}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Moneyline */}
      {(homeClutchScore || awayClutchScore || moneyline) && (
        <div className="popover-section moneyline">
          <div className="scoreboard-row">
            <div className="scoreboard-label">
              <FaDollarSign className="odds-icon" />
              Moneyline
            </div>
            <div className="scoreboard-value">{moneyline ?? "—"}</div>
          </div>
        </div>
      )}

      {/* Clutch breakdown */}
      {(homeClutchBreakdown || awayClutchBreakdown) && (
        <div className="popover-section">
          <table className="clutch-table">
            <thead>
              <tr>
                <th>Away</th>
                <th>Category</th>
                <th>Home</th>
              </tr>
            </thead>
            <tbody>
              <tr className="clutch-score-row">
                <td className={typeof awayClutchScore === 'number' && typeof homeClutchScore === 'number' && awayClutchScore > homeClutchScore ? 'hype-highlight' : ''}>
                  <strong>{awayClutchScore?.toFixed(1) ?? "—"}</strong>
                </td>
                <td className="clutch-category">
                  <strong>Hype Score</strong>
                </td>
                <td className={typeof awayClutchScore === 'number' && typeof homeClutchScore === 'number' && homeClutchScore > awayClutchScore ? 'hype-highlight' : ''}>
                  <strong>{homeClutchScore?.toFixed(1) ?? "—"}</strong>
                </td>
              </tr>

              {[
                "last10", 
                "winStreak", 
                "runDiff", 
                "injuries", 
                "hypeNews", 
                "antiHype",
                "eliteBonus",
                "dominanceBonus",
                "underdogBoost",
                "homeFieldBonus",
                "roadWarriorBonus",
                "balancedTeamBonus",
                "fatiguePenalty",
                "divisionFavoriteBonus",
                "wildcardChaserBonus",
              ].map((key) => {
                let awayVal = key.includes("Hits")
                  ? awayClutchBreakdown?.[key]?.length ?? 0
                  : awayClutchBreakdown?.[key] ?? "—";
                let homeVal = key.includes("Hits")
                  ? homeClutchBreakdown?.[key]?.length ?? 0
                  : homeClutchBreakdown?.[key] ?? "—";

                // Special case: if last10, split like "6-4" into wins
                if (key === "last10") {
                  awayVal = typeof awayVal === "string" ? parseInt(awayVal.split("-")[0], 10) : awayVal;
                  homeVal = typeof homeVal === "string" ? parseInt(homeVal.split("-")[0], 10) : homeVal;
                }

                awayVal = typeof awayVal === "string" ? parseFloat(awayVal) : awayVal;
                homeVal = typeof homeVal === "string" ? parseFloat(homeVal) : homeVal;

                const awayClass = typeof awayVal === 'number'
                ? awayVal === 0
                  ? '' // No class if 0
                  : awayVal <= -5
                    ? 'anti-hype-highlight'
                    : awayVal > (homeVal ?? -Infinity)
                      ? 'hype-highlight'
                      : ''
                : '';
              
              const homeClass = typeof homeVal === 'number'
                ? homeVal === 0
                  ? '' // No class if 0
                  : homeVal <= -5
                    ? 'anti-hype-highlight'
                    : homeVal > (awayVal ?? -Infinity)
                      ? 'hype-highlight'
                      : ''
                : '';
              

                return (
                  <tr key={key}>
                    <td className={awayClass}>{awayVal}</td>
                    <td className="clutch-category">{LABELS[key] ?? key}</td>
                    <td className={homeClass}>{homeVal}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default GamePopoverCard;
